/** A point in SVG user space, y pointing down. */
export type Point = { x: number; y: number };

/** Unit vector from `from` towards `to`; a zero-length step points along +x rather than dividing by zero. */
export const unitTowards = (from: Point, to: Point): Point => {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const len = Math.hypot(dx, dy) || 1;
  return { x: dx / len, y: dy / len };
};

/** Rounds to two decimal places, which is as fine as an SVG path needs. */
export const round2 = (n: number): number => Math.round(n * 100) / 100;

/**
 * Moves every vertex of a convex clockwise polygon `inset` px inward, along its corner bisector.
 * Shifting by `inset / sin(θ/2)` — not by `inset` — keeps each edge, not just each vertex, that far
 * from the original.
 */
export function insetPolygon(points: Point[], inset: number): Point[] {
  return points.map((cur, i) => {
    const toPrev = unitTowards(cur, points[(i - 1 + points.length) % points.length]);
    const toNext = unitTowards(cur, points[(i + 1) % points.length]);
    const bisector = { x: toPrev.x + toNext.x, y: toPrev.y + toNext.y };
    const len = Math.hypot(bisector.x, bisector.y) || 1;
    // |bisector| is 2·cos(θ/2) for the two unit vectors, so len/2 is cos(θ/2) and this is sin(θ/2).
    const sinHalf = Math.sqrt(Math.max(0, 1 - (len / 2) ** 2));
    const shift = inset / (sinHalf || 1);
    return { x: cur.x + (bisector.x / len) * shift, y: cur.y + (bisector.y / len) * shift };
  });
}

/**
 * Closed path around a convex clockwise polygon with every corner rounded. Corners are trimmed back
 * along both edges and bridged with a circular arc, the trim shrinking where an edge is too short to
 * take the full radius. A corner given a radius of 0 stays square.
 */
export function roundedPolygonPath(points: Point[], radius: number | number[]): string {
  const parts: string[] = [];

  for (let i = 0; i < points.length; i++) {
    const cur = points[i];
    const prev = points[(i - 1 + points.length) % points.length];
    const next = points[(i + 1) % points.length];
    const toPrev = unitTowards(cur, prev);
    const toNext = unitTowards(cur, next);

    const cos = Math.max(-1, Math.min(1, toPrev.x * toNext.x + toPrev.y * toNext.y));
    const tanHalf = Math.tan(Math.acos(cos) / 2);
    const maxTrim =
      Math.min(Math.hypot(prev.x - cur.x, prev.y - cur.y), Math.hypot(next.x - cur.x, next.y - cur.y)) / 2;
    const trim = Math.min((Array.isArray(radius) ? radius[i] : radius) / tanHalf, maxTrim);
    const arcRadius = trim * tanHalf;

    const start = { x: cur.x + toPrev.x * trim, y: cur.y + toPrev.y * trim };
    const end = { x: cur.x + toNext.x * trim, y: cur.y + toNext.y * trim };

    parts.push(
      `${i === 0 ? 'M' : 'L'} ${round2(start.x)} ${round2(start.y)}`,
      `A ${round2(arcRadius)} ${round2(arcRadius)} 0 0 1 ${round2(end.x)} ${round2(end.y)}`,
    );
  }

  parts.push('Z');
  return parts.join(' ');
}

/** Positive when the points wind clockwise on screen (y down). */
export const signedArea = (points: Point[]): number =>
  points.reduce((sum, p, i) => {
    const q = points[(i + 1) % points.length];
    return sum + p.x * q.y - q.x * p.y;
  }, 0);

/**
 * The points wound clockwise on screen. Shapes filled together should share a winding: under the
 * nonzero fill rule, overlapping shapes of opposite winding cancel and punch holes where they meet.
 */
export const clockwise = (points: Point[]): Point[] => (signedArea(points) < 0 ? points.slice().reverse() : points);

export const polygonPath = (points: Point[]): string =>
  `M ${points.map((p) => `${round2(p.x)} ${round2(p.y)}`).join(' L ')} Z`;
